import { type ComponentType, lazy, type LazyExoticComponent } from 'react';

import type { ModalComponentCommonProps, ModalComponentProps, ModalType } from '../props';

import type { ModalErrorContext, ModalErrorReporter } from '../errors';
import { ModalLoadError } from './ModalLoadError';

export const DEFAULT_MODAL_LOAD_TIMEOUT_MS = 30000;

export type ModalLoader<T extends ModalType = ModalType> = () => Promise<{
  default: ComponentType<ModalComponentProps<T>>;
}>;

export type ModalLoaders = { [T in ModalType]?: ModalLoader<T> };

export interface ModalScope {
  namespace: string;
  register: <T extends ModalType>(type: T, loader: ModalLoader<T>) => () => void;
  registerMany: (loaders: ModalLoaders) => () => void;
  dispose: () => void;
}

export type RegisteredModalProps = ModalComponentCommonProps & Record<string, unknown>;

type RegisteredModalComponent = LazyExoticComponent<ComponentType<RegisteredModalProps>>;

type RegisteredModalModule = { default: ComponentType<RegisteredModalProps> };

export interface ModalRegistryEntry {
  readonly component: RegisteredModalComponent;
  readonly source: string;
}

export type ModalRegistryChange = { type: ModalType; kind: 'registered' | 'unregistered' };

export type ModalRegistryListener = (change: ModalRegistryChange) => void;

export interface ModalRegistry {
  register: <T extends ModalType>(type: T, loader: ModalLoader<T>) => () => void;
  registerMany: (loaders: ModalLoaders) => () => void;
  unregister: (type: ModalType) => void;
  getEntry: (type: ModalType) => ModalRegistryEntry | undefined;
  subscribe: (listener: ModalRegistryListener) => () => void;
  createScope: (namespace: string) => ModalScope;
}

export interface ModalRegistryOptions {
  isDev?: boolean;
  reportError?: ModalErrorReporter;
  loadTimeoutMs?: number;
  hostSource?: string;
}

interface ModalOwner {
  scope: ModalScope;
  owns: (type: string) => boolean;
}

interface ModalRecord {
  entry: ModalRegistryEntry;
  loader: ModalLoader;
  owner: ModalOwner;
}

interface CachedModalComponent {
  loader: ModalLoader;
  component: RegisteredModalComponent;
}

type ModalRegistrationViolation = [Error, ModalErrorContext];

const NAMESPACE_PATTERN = /^[^/]+$/;

const noop = () => {};

const ignoreError: ModalErrorReporter = () => {};

export const createModalRegistry = ({
  isDev = false,
  reportError = ignoreError,
  loadTimeoutMs = DEFAULT_MODAL_LOAD_TIMEOUT_MS,
  hostSource = 'host',
}: ModalRegistryOptions = {}): ModalRegistry => {
  const records = new Map<ModalType, ModalRecord>();
  const components = new Map<ModalType, CachedModalComponent>();
  const activeNamespaces = new Set<string>();
  const disposedOwners = new WeakSet<ModalOwner>();
  const listeners = new Set<ModalRegistryListener>();

  const notify = (change: ModalRegistryChange) => {
    listeners.forEach((listener) => listener(change));
  };

  const rejectRegistration = ([error, context]: ModalRegistrationViolation) => {
    if (isDev) throw error;

    reportError(error, context);
  };

  const removeRecord = (type: ModalType, record: ModalRecord) => {
    if (records.get(type) !== record) return;

    records.delete(type);
    notify({ type, kind: 'unregistered' });
  };

  const dropFailedComponent = (type: ModalType, failed: RegisteredModalComponent | undefined) => {
    if (failed && components.get(type)?.component === failed) components.delete(type);
  };

  const loadWithTimeout = (type: ModalType, loader: ModalLoader) => () =>
    new Promise<RegisteredModalModule>((resolve, reject) => {
      const started = components.get(type)?.component;
      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      const fail = (error: unknown) => {
        clearTimeout(timeoutId);
        dropFailedComponent(type, started);
        reject(error instanceof ModalLoadError ? error : new ModalLoadError(type, error));
      };

      timeoutId = setTimeout(() => {
        fail(new ModalLoadError(type, new Error(`Loading took longer than ${loadTimeoutMs} ms.`)));
      }, loadTimeoutMs);

      let loading: Promise<RegisteredModalModule>;

      try {
        loading = (loader as unknown as () => Promise<RegisteredModalModule>)();
      } catch (error) {
        fail(error);

        return;
      }

      loading.then((module) => {
        clearTimeout(timeoutId);
        resolve(module);
      }, fail);
    });

  const getComponent = (type: ModalType, loader: ModalLoader) => {
    const cached = components.get(type);

    if (cached?.loader === loader) return cached.component;

    const component: RegisteredModalComponent = lazy(loadWithTimeout(type, loader));

    components.set(type, { loader, component });

    return component;
  };

  const getEntry = (type: ModalType) => {
    const record = records.get(type);

    if (!record) return undefined;

    const component = getComponent(type, record.loader);

    if (record.entry.component !== component) record.entry = { component, source: record.entry.source };

    return record.entry;
  };

  const findViolation = (owner: ModalOwner, type: ModalType): ModalRegistrationViolation | undefined => {
    const { namespace } = owner.scope;

    if (!owner.owns(type)) {
      return [
        new Error(`Modal type "${type}" is outside of "${namespace}", "${namespace}" was ignored.`),
        { reason: 'namespace', modalType: type, source: namespace },
      ];
    }

    const existing = records.get(type);

    if (!existing || existing.owner === owner) return undefined;

    return [
      new Error(
        `Modal type "${type}" is already registered by "${existing.owner.scope.namespace}", "${namespace}" was ignored.`
      ),
      { reason: 'collision', modalType: type, source: namespace },
    ];
  };

  const addRecord = (owner: ModalOwner, type: ModalType, loader: ModalLoader) => {
    const record: ModalRecord = {
      entry: { component: getComponent(type, loader), source: owner.scope.namespace },
      loader,
      owner,
    };

    records.set(type, record);
    notify({ type, kind: 'registered' });

    return () => removeRecord(type, record);
  };

  const registerFor = (owner: ModalOwner, type: ModalType, loader: ModalLoader) => {
    if (disposedOwners.has(owner)) return noop;

    const violation = findViolation(owner, type);

    if (violation) {
      rejectRegistration(violation);

      return noop;
    }

    return addRecord(owner, type, loader);
  };

  const registerManyFor = (owner: ModalOwner, loaders: ModalLoaders) => {
    if (disposedOwners.has(owner)) return noop;

    const types = Object.keys(loaders) as ModalType[];
    const violations = types.map((type) => findViolation(owner, type));
    const firstViolation = violations.find(Boolean);

    if (isDev && firstViolation) throw firstViolation[0];

    const unregisters = types.map((type, index) => {
      const violation = violations[index];

      if (violation) {
        rejectRegistration(violation);

        return noop;
      }

      return addRecord(owner, type, loaders[type] as ModalLoader);
    });

    return () => unregisters.forEach((unregister) => unregister());
  };

  const disposeOwner = (owner: ModalOwner) => {
    if (disposedOwners.has(owner)) return;

    disposedOwners.add(owner);
    activeNamespaces.delete(owner.scope.namespace);

    Array.from(records.entries())
      .filter(([, record]) => record.owner === owner)
      .forEach(([type, record]) => removeRecord(type, record));
  };

  const createOwner = (namespace: string, owns: ModalOwner['owns']): ModalOwner => {
    const owner: ModalOwner = {
      owns,
      scope: {
        namespace,
        register: (type, loader) => registerFor(owner, type, loader as ModalLoader),
        registerMany: (loaders) => registerManyFor(owner, loaders),
        dispose: () => disposeOwner(owner),
      },
    };

    return owner;
  };

  const findScopeViolation = (namespace: string): ModalRegistrationViolation | undefined => {
    if (!NAMESPACE_PATTERN.test(namespace)) {
      return [
        new Error(`Modal scope namespace "${namespace}" must be non-empty and contain no "/".`),
        { reason: 'namespace', source: namespace },
      ];
    }

    if (!activeNamespaces.has(namespace)) return undefined;

    return [
      new Error(`Modal scope "${namespace}" is already active, dispose it before creating it again.`),
      { reason: 'collision', source: namespace },
    ];
  };

  const createScope = (namespace: string): ModalScope => {
    const owner = createOwner(
      namespace,
      (type) => type.startsWith(`${namespace}/`) && type.length > namespace.length + 1
    );
    const violation = findScopeViolation(namespace);

    if (violation) {
      rejectRegistration(violation);
      disposedOwners.add(owner);

      return owner.scope;
    }

    activeNamespaces.add(namespace);

    return owner.scope;
  };

  const hostOwner = createOwner(hostSource, (type) => !type.includes('/'));

  return {
    register: hostOwner.scope.register,
    registerMany: hostOwner.scope.registerMany,
    unregister: (type) => {
      const record = records.get(type);

      if (record?.owner === hostOwner) removeRecord(type, record);
    },
    getEntry,
    subscribe: (listener) => {
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    },
    createScope,
  };
};
